// src/components/CommissionsList.js
import React, { useState, useEffect, useMemo } from 'react';
import { subscribeToCommissions, markCommissionAsPaid, markCommissionAsPending, createCommission, deleteCommission } from '../services/commissionsService';
import { useTecnicos } from '../hooks/useTecnicos';
import AddCommissionModal from './AddCommissionModal';

// ─── Helpers de fecha ──────────────────────────────────────────────────────────
const parseDate = (str) => {
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return new Date(str + 'T00:00:00');
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split('/');
    return new Date(`${y}-${m}-${d}T00:00:00`);
  }
  try { return new Date(str); } catch { return null; }
};

const formatDate = (str) => {
  if (!str) return 'Sin fecha';
  try {
    if (str?.toDate) return str.toDate().toLocaleDateString('es-ES');
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) { const [y, m, d] = str.split('-'); return `${d}/${m}/${y}`; }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return str;
    return new Date(str).toLocaleDateString('es-ES');
  } catch { return 'Fecha inválida'; }
};

const formatCurrency = (n) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);

const getEstadoColor = (estado) =>
  estado === 'PAGADA' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800';

const getEstadoTexto = (estado) => estado === 'PAGADA' ? 'Pagada' : 'Pendiente';

// ─── Componente ────────────────────────────────────────────────────────────────
const CommissionsList = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [updatingId, setUpdatingId]   = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId]   = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filtros, setFiltros]         = useState({ tecnico: 'todos', periodo: 'todos', estado: 'todos' });
  const { tecnicos } = useTecnicos();
  const getTecnicoNombre = (id) =>
    tecnicos.find(t => String(t.employeeId) === String(id))?.nombre || null;

  // Suscripción en tiempo real
  useEffect(() => {
    const unsub = subscribeToCommissions(
      (data) => { setCommissions(data); setLoading(false); setError(null); },
      ()     => { setError('Error al cargar las comisiones'); setLoading(false); }
    );
    return unsub;
  }, []);

  // ── Stats globales (sobre todas las comisiones, sin filtrar) ───────────────
  const stats = useMemo(() => {
    const pendientes = commissions.filter(c => c.estado !== 'PAGADA');
    const pagadas    = commissions.filter(c => c.estado === 'PAGADA');
    const byTecnico  = tecnicos.map(tec => {
      const mine = commissions.filter(c => String(c.employeeId) === String(tec.employeeId));
      return {
        ...tec,
        count:            mine.length,
        pendientes:       mine.filter(c => c.estado !== 'PAGADA').length,
        pagadas:          mine.filter(c => c.estado === 'PAGADA').length,
        importe:          mine.reduce((s, c) => s + (c.importe || 0), 0),
        importePendiente: mine.filter(c => c.estado !== 'PAGADA').reduce((s, c) => s + (c.importe || 0), 0),
      };
    }).filter(t => t.count > 0);
    return {
      pendientes:   { count: pendientes.length, importe: pendientes.reduce((s,c)=>s+(c.importe||0),0) },
      pagadas:      { count: pagadas.length,    importe: pagadas.reduce((s,c)=>s+(c.importe||0),0)    },
      totalImporte: commissions.reduce((s,c)=>s+(c.importe||0),0),
      byTecnico,
    };
  }, [commissions, tecnicos]);

  // ── Filas filtradas (para la tabla) ───────────────────────────────────────
  const comisionesFiltradas = useMemo(() => {
    const hoy = new Date(); hoy.setHours(23,59,59,999);
    let inicio = null;
    if (filtros.periodo !== 'todos') {
      inicio = new Date();
      if      (filtros.periodo === 'hoy')    inicio.setHours(0,0,0,0);
      else if (filtros.periodo === 'semana') { inicio.setDate(inicio.getDate()-7); inicio.setHours(0,0,0,0); }
      else if (filtros.periodo === 'mes')    { inicio.setDate(inicio.getDate()-30); inicio.setHours(0,0,0,0); }
    }
    return commissions.filter(c => {
      if (filtros.tecnico !== 'todos' && c.employeeId !== Number(filtros.tecnico)) return false;
      if (filtros.estado  !== 'todos' && c.estado      !== filtros.estado)         return false;
      if (inicio) {
        const d = parseDate(c.fechaCreacion);
        if (!d || d < inicio || d > hoy) return false;
      }
      return true;
    });
  }, [commissions, filtros]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleAddCommission = async (data) => {
    await createCommission(data);
    // La suscripción actualiza la lista automáticamente
  };

  const handleToggle = async (commission) => {
    setUpdatingId(commission.id);
    try {
      if (commission.estado === 'PAGADA') await markCommissionAsPending(commission.id);
      else                                await markCommissionAsPaid(commission.id);
    } catch { setError('Error al actualizar el estado'); }
    finally { setUpdatingId(null); }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteCommission(id);
      setConfirmDeleteId(null);
    } catch { setError('Error al eliminar la comisión'); }
    finally { setDeletingId(null); }
  };

  const filtrosActivos = [
    filtros.tecnico !== 'todos',
    filtros.periodo !== 'todos',
    filtros.estado  !== 'todos',
  ].filter(Boolean).length;

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" />
          <span className="ml-3 text-gray-600">Cargando comisiones...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <div className="text-center py-12 text-red-500">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AddCommissionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddCommission}
      />

      {/* Panel de estadísticas globales */}
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">📊 Resumen global</h3>

        {/* 3 tarjetas principales */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-amber-800 text-sm">⏳ Pendientes</h4>
              <span className="bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full text-xs font-bold">
                {stats.pendientes.count}
              </span>
            </div>
            <div className="text-2xl font-bold text-amber-700">{formatCurrency(stats.pendientes.importe)}</div>
            <div className="text-xs text-amber-600 mt-1">Por pagar</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-green-800 text-sm">✅ Pagadas</h4>
              <span className="bg-green-200 text-green-800 px-2 py-0.5 rounded-full text-xs font-bold">
                {stats.pagadas.count}
              </span>
            </div>
            <div className="text-2xl font-bold text-green-700">{formatCurrency(stats.pagadas.importe)}</div>
            <div className="text-xs text-green-600 mt-1">Ya pagado</div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-gray-700 text-sm">💰 Total</h4>
              <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs font-bold">
                {commissions.length}
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{formatCurrency(stats.totalImporte)}</div>
            <div className="text-xs text-gray-500 mt-1">Acumulado total</div>
          </div>
        </div>

        {/* Desglose por técnico */}
        {stats.byTecnico.length > 0 && (
          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-600 mb-3">Desglose por técnico</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {stats.byTecnico.map(tec => (
                <div key={tec.id} className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                  <p className="font-bold text-sm text-gray-800 mb-1">{tec.nombre}</p>
                  <p className="text-lg font-bold text-gray-900">{formatCurrency(tec.importe)}</p>
                  <div className="flex gap-2 text-xs mt-1.5">
                    <span className="text-amber-600 font-medium">{tec.pendientes} pend.</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-green-600 font-medium">{tec.pagadas} pag.</span>
                  </div>
                  {tec.importePendiente > 0 && (
                    <p className="text-xs text-amber-600 mt-1">{formatCurrency(tec.importePendiente)} pendiente</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lista con filtros */}
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        {/* Cabecera + botón nueva */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Historial de comisiones</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {comisionesFiltradas.length} de {commissions.length} comisiones
              {filtrosActivos > 0 && <span className="ml-1 text-red-600">({filtrosActivos} filtro{filtrosActivos > 1 ? 's' : ''} activo{filtrosActivos > 1 ? 's' : ''})</span>}
            </p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm font-semibold"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nueva Comisión
          </button>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 mb-4">
          {/* Técnico */}
          <select
            value={filtros.tecnico}
            onChange={e => setFiltros(p => ({ ...p, tecnico: e.target.value }))}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-red-500 focus:outline-none bg-white"
          >
            <option value="todos">👤 Todos</option>
            {tecnicos.map(t => (
              <option key={t.id} value={t.employeeId}>{t.nombre}</option>
            ))}
          </select>

          {/* Período */}
          <select
            value={filtros.periodo}
            onChange={e => setFiltros(p => ({ ...p, periodo: e.target.value }))}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-red-500 focus:outline-none bg-white"
          >
            <option value="todos">📅 Todo el tiempo</option>
            <option value="hoy">Hoy</option>
            <option value="semana">Última semana</option>
            <option value="mes">Últimos 30 días</option>
          </select>

          {/* Estado */}
          <select
            value={filtros.estado}
            onChange={e => setFiltros(p => ({ ...p, estado: e.target.value }))}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-red-500 focus:outline-none bg-white"
          >
            <option value="todos">Todos los estados</option>
            <option value="PENDIENTE">⏳ Pendientes</option>
            <option value="PAGADA">✅ Pagadas</option>
          </select>

          {filtrosActivos > 0 && (
            <button
              onClick={() => setFiltros({ tecnico: 'todos', periodo: 'todos', estado: 'todos' })}
              className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            >
              ✕ Limpiar
            </button>
          )}
        </div>

        {/* Tabla */}
        {comisionesFiltradas.length === 0 ? (
          <div className="text-center py-14">
            <div className="text-5xl mb-4">💰</div>
            <p className="text-lg font-semibold text-gray-600">
              {commissions.length === 0 ? 'Sin comisiones registradas' : 'Sin resultados para los filtros aplicados'}
            </p>
            {commissions.length === 0 && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-4 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 text-sm font-medium"
              >
                + Nueva Comisión
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full table-auto min-w-[640px]">
              <thead>
                <tr className="bg-gray-50 border-b text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  <th className="text-left p-3">Fecha</th>
                  <th className="text-left p-3">Técnico</th>
                  <th className="text-left p-3 hidden sm:table-cell">Tipo</th>
                  <th className="text-left p-3 hidden md:table-cell">Descripción</th>
                  <th className="text-right p-3">Importe</th>
                  <th className="text-center p-3">Estado</th>
                  <th className="text-center p-3 hidden sm:table-cell">Orden</th>
                  <th className="text-center p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {comisionesFiltradas.map((commission) => {
                  const isUpdating  = updatingId === commission.id;
                  const isDeleting  = deletingId === commission.id;
                  const isConfirming = confirmDeleteId === commission.id;
                  const nombreTec   = getTecnicoNombre(commission.employeeId) || commission.employeeName || `ID:${commission.employeeId}`;

                  return (
                    <tr key={commission.id} className={`border-b transition-colors ${isConfirming ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <td className="p-3 text-sm text-gray-700 whitespace-nowrap">
                        {formatDate(commission.fechaCreacion)}
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-medium text-gray-800">{nombreTec}</span>
                      </td>
                      <td className="p-3 hidden sm:table-cell">
                        <span className="text-sm text-gray-700">{commission.tipoString || commission.tipo || '—'}</span>
                      </td>
                      <td className="p-3 hidden md:table-cell">
                        <span className="text-sm text-gray-500 max-w-[200px] truncate block" title={commission.descripcion}>
                          {commission.descripcion || '—'}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <span className="text-sm font-bold text-green-600">{formatCurrency(commission.importe)}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getEstadoColor(commission.estado)}`}>
                          {getEstadoTexto(commission.estado)}
                        </span>
                      </td>
                      <td className="p-3 text-center text-sm text-gray-500 hidden sm:table-cell">
                        {commission.workorderId || '—'}
                      </td>
                      <td className="p-3">
                        {isConfirming ? (
                          <div className="flex items-center gap-1.5 justify-center">
                            <span className="text-xs text-red-700 font-medium whitespace-nowrap">¿Eliminar?</span>
                            <button
                              onClick={() => handleDelete(commission.id)}
                              disabled={isDeleting}
                              className="px-2 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50 font-semibold"
                            >
                              {isDeleting ? '...' : 'Sí'}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg hover:bg-gray-200 font-semibold"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 justify-center">
                            {/* Toggle estado */}
                            <button
                              onClick={() => handleToggle(commission)}
                              disabled={isUpdating}
                              title={commission.estado === 'PAGADA' ? 'Marcar como pendiente' : 'Marcar como pagada'}
                              className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-40 ${
                                commission.estado === 'PAGADA'
                                  ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                                  : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                              }`}
                            >
                              {isUpdating
                                ? <span className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                : commission.estado === 'PAGADA' ? '↩ Pend.' : '✓ Pagar'}
                            </button>
                            {/* Eliminar */}
                            <button
                              onClick={() => setConfirmDeleteId(commission.id)}
                              title="Eliminar comisión"
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionsList;
